/** Admin dialog for creating / styling a custom dashboard section: title, icon and colour. */
import React, { useEffect, useState } from 'react';
import { Folder, Wallet, Wrench, GraduationCap, Package, Users, Truck, Megaphone, BarChart3, Settings, Star, Hammer, Paintbrush, ClipboardList, Bell, Landmark } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export const SECTION_ICONS = { folder: Folder, wallet: Wallet, wrench: Wrench, graduation: GraduationCap, package: Package, users: Users, truck: Truck, megaphone: Megaphone, chart: BarChart3, settings: Settings, star: Star, hammer: Hammer, paintbrush: Paintbrush, clipboard: ClipboardList, bell: Bell, landmark: Landmark };
export const SECTION_COLOURS = { slate: ['bg-slate-200', 'text-slate-700'], purple: ['bg-purple-100', 'text-purple-700'], sky: ['bg-sky-100', 'text-sky-700'], emerald: ['bg-emerald-100', 'text-emerald-700'], amber: ['bg-amber-100', 'text-amber-700'], rose: ['bg-rose-100', 'text-rose-700'], orange: ['bg-orange-100', 'text-orange-700'], indigo: ['bg-indigo-100', 'text-indigo-700'], teal: ['bg-teal-100', 'text-teal-700'], pink: ['bg-pink-100', 'text-pink-700'] };

export function sectionIcon(iconKey, colourKey) {
  const Icon = SECTION_ICONS[iconKey] || Folder;
  const [bg, fg] = SECTION_COLOURS[colourKey] || SECTION_COLOURS.slate;
  return { accent: bg, icon: <Icon className={`w-3.5 h-3.5 ${fg}`} /> };
}

export function SectionStyleDialog({ open, onClose, onSave, initial, mode }) {
  const [title, setTitle] = useState('');
  const [icon, setIcon] = useState('folder');
  const [colour, setColour] = useState('slate');
  const [description, setDescription] = useState('');
  useEffect(() => { if (open) { setTitle(initial?.title || ''); setIcon(initial?.icon || 'folder'); setColour(initial?.colour || 'slate'); setDescription(initial?.description || ''); } }, [open, initial]);
  const styleable = mode !== 'rename-builtin';
  const [bg, fg] = SECTION_COLOURS[colour] || SECTION_COLOURS.slate;
  const Preview = SECTION_ICONS[icon] || Folder;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="section-style-dialog">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'New section' : 'Edit section'}</DialogTitle>
          <DialogDescription>{styleable ? 'Everyone sees this section. Pick a name, icon and colour so it stands out.' : 'Rename this built-in section and its description for everyone.'}</DialogDescription>
        </DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); if (title.trim()) onSave({ title: title.trim(), icon, colour, description: description.trim() }); }} className="space-y-4">
          <div className="flex items-center gap-3">
            <span className={`inline-flex items-center justify-center w-9 h-9 rounded-full ${bg}`} data-testid="ssd-preview"><Preview className={`w-4 h-4 ${fg}`} /></span>
            <div className="flex-1"><Label htmlFor="ssd-title">Section name</Label><Input id="ssd-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Finance" autoFocus data-testid="ssd-title" /></div>
          </div>
          <div>
            <div className="flex items-baseline justify-between"><Label htmlFor="ssd-description">Description <span className="text-gray-400 font-normal">(optional)</span></Label><span className="text-[11px] text-gray-400" data-testid="ssd-description-count">{description.length}/140</span></div>
            <Input id="ssd-description" value={description} onChange={(e) => setDescription(e.target.value.slice(0, 140))} maxLength={140} placeholder="One line telling new staff what lives here" data-testid="ssd-description" />
          </div>
          {styleable && (
            <>
              <div>
                <Label>Icon</Label>
                <div className="grid grid-cols-8 gap-1.5 mt-1">
                  {Object.entries(SECTION_ICONS).map(([k, I]) => (
                    <button key={k} type="button" onClick={() => setIcon(k)} aria-label={k} data-testid={`ssd-icon-${k}`} className={`h-9 rounded-md border inline-flex items-center justify-center ${icon === k ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-200 text-gray-600 hover:border-gray-400'}`}><I className="w-4 h-4" /></button>
                  ))}
                </div>
              </div>
              <div>
                <Label>Colour</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {Object.entries(SECTION_COLOURS).map(([k, [b]]) => (
                    <button key={k} type="button" onClick={() => setColour(k)} aria-label={k} data-testid={`ssd-colour-${k}`} className={`w-7 h-7 rounded-full ${b} border-2 ${colour === k ? 'border-gray-900 scale-110' : 'border-transparent'} transition-transform`} />
                  ))}
                </div>
              </div>
            </>
          )}
          <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={onClose} data-testid="ssd-cancel">Cancel</Button><Button type="submit" disabled={!title.trim()} className="bg-gray-900 text-white" data-testid="ssd-save">{mode === 'create' ? 'Create section' : 'Save'}</Button></div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
